import { 
  Users, Package, ShoppingCart, Truck, DollarSign, FileText, 
  Settings, BarChart3, Wrench, Building2, Warehouse, Receipt,
  ClipboardList, UserCheck, MapPin, Calculator, CreditCard,
  FileSpreadsheet, Bell, Shield, ArrowLeft, Layers
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface ModuleInfo {
  name: string;
  description: string;
  icon: React.ElementType;
  features: string[];
  color: string;
}

interface CategoryInfo {
  name: string;
  modules: ModuleInfo[];
}

const categories: CategoryInfo[] = [
  {
    name: 'Cadastros',
    modules: [
      {
        name: 'Clientes',
        description: 'Gestão completa de clientes e fornecedores',
        icon: Users,
        features: ['Dados cadastrais', 'Endereços múltiplos', 'Histórico de compras', 'Score de crédito'],
        color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
      },
      {
        name: 'Colaboradores',
        description: 'Controle de funcionários e permissões',
        icon: UserCheck,
        features: ['Perfis de acesso', 'Dados pessoais', 'Comissões', 'Metas'],
        color: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400'
      },
      {
        name: 'Transportadoras',
        description: 'Cadastro de transportadoras parceiras',
        icon: Truck,
        features: ['Dados da empresa', 'Veículos', 'Frete padrão', 'Cobertura'],
        color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400'
      }
    ]
  },
  {
    name: 'Produtos e Estoque',
    modules: [
      {
        name: 'Produtos',
        description: 'Catálogo completo de produtos',
        icon: Package,
        features: ['Preços e custos', 'Categorias', 'Código de barras', 'Imagens'],
        color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
      },
      {
        name: 'Equipamentos',
        description: 'Controle de ativos e máquinas',
        icon: Wrench,
        features: ['Número de série', 'Status', 'Manutenção', 'Localização'],
        color: 'bg-teal-500/10 text-teal-600 dark:text-teal-400'
      },
      {
        name: 'Depósitos',
        description: 'Gestão de estoques e armazéns',
        icon: Warehouse,
        features: ['Multi-depósitos', 'Transferências', 'Inventário', 'Saldo por local'],
        color: 'bg-green-500/10 text-green-600 dark:text-green-400'
      }
    ]
  },
  {
    name: 'Comercial',
    modules: [
      {
        name: 'Vendas',
        description: 'Gestão de pedidos e vendas',
        icon: ShoppingCart,
        features: ['PDV', 'Orçamentos', 'Tabelas de preço', 'Descontos'],
        color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
      },
      {
        name: 'Compras',
        description: 'Controle de aquisições',
        icon: ClipboardList,
        features: ['Pedidos de compra', 'Cotações', 'Entrada de mercadorias', 'Custo médio'],
        color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
      },
      {
        name: 'Locações',
        description: 'Aluguel de equipamentos e cilindros',
        icon: Building2,
        features: ['Contratos', 'Devolução', 'Faturamento', 'Renovação'],
        color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
      },
      {
        name: 'Comodatos',
        description: 'Empréstimo de ativos',
        icon: Layers,
        features: ['Controle de cessão', 'Termos', 'Devoluções', 'Rastreamento'],
        color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400'
      }
    ]
  },
  {
    name: 'Logística',
    modules: [
      {
        name: 'Entregas',
        description: 'Gestão de rotas e entregas',
        icon: MapPin,
        features: ['Roteirização', 'Status em tempo real', 'Assinatura digital', 'Fotos'],
        color: 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
      },
      {
        name: 'Motoristas',
        description: 'App exclusivo para entregadores',
        icon: Truck,
        features: ['Lista de entregas', 'Navegação GPS', 'Comprovantes', 'Check-in'],
        color: 'bg-red-500/10 text-red-600 dark:text-red-400'
      }
    ]
  },
  {
    name: 'Financeiro',
    modules: [
      {
        name: 'Contas a Receber',
        description: 'Gestão de recebíveis',
        icon: DollarSign,
        features: ['Títulos', 'Baixas', 'Cobrança', 'Boletos'],
        color: 'bg-lime-500/10 text-lime-600 dark:text-lime-400'
      },
      {
        name: 'Contas a Pagar',
        description: 'Controle de pagamentos',
        icon: CreditCard,
        features: ['Despesas', 'Fornecedores', 'Parcelamentos', 'Vencimentos'],
        color: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
      },
      {
        name: 'Fluxo de Caixa',
        description: 'Visão financeira consolidada',
        icon: Calculator,
        features: ['Projeções', 'DRE', 'Saldo por conta', 'Centros de custo'],
        color: 'bg-sky-500/10 text-sky-600 dark:text-sky-400'
      }
    ]
  },
  {
    name: 'Fiscal',
    modules: [
      {
        name: 'Notas Fiscais',
        description: 'Emissão de NF-e e NFC-e',
        icon: FileText,
        features: ['Emissão automática', 'Cancelamento', 'Carta de correção', 'XML'],
        color: 'bg-slate-500/10 text-slate-600 dark:text-slate-400'
      },
      {
        name: 'Relatórios Fiscais',
        description: 'Documentos para contabilidade',
        icon: FileSpreadsheet,
        features: ['SPED', 'Livros fiscais', 'Exportação', 'Conciliação'],
        color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400'
      }
    ]
  },
  {
    name: 'CRM e Serviços',
    modules: [
      {
        name: 'CRM',
        description: 'Relacionamento com clientes',
        icon: Users,
        features: ['Pipeline', 'Interações', 'Rotinas de visita', 'Carteira'],
        color: 'bg-pink-500/10 text-pink-600 dark:text-pink-400'
      },
      {
        name: 'Ordens de Serviço',
        description: 'Gestão de serviços técnicos',
        icon: Wrench,
        features: ['Abertura de OS', 'Técnicos', 'Peças', 'Histórico'],
        color: 'bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400'
      }
    ]
  },
  {
    name: 'Administração',
    modules: [
      {
        name: 'Relatórios',
        description: 'Análises e dashboards',
        icon: BarChart3,
        features: ['Vendas', 'Estoque', 'Financeiro', 'Exportação'],
        color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
      },
      {
        name: 'Configurações',
        description: 'Parâmetros do sistema',
        icon: Settings,
        features: ['Empresa', 'Usuários', 'Integrações', 'Backup'],
        color: 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
      },
      {
        name: 'Auditoria',
        description: 'Logs e rastreabilidade',
        icon: Shield,
        features: ['Histórico de ações', 'Usuários', 'Alterações', 'Segurança'],
        color: 'bg-stone-500/10 text-stone-600 dark:text-stone-400'
      }
    ]
  }
];

const ModulesPresentation = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      {/* Header */}
      <header className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                Sistema de Gestão Integrada
              </h1>
              <p className="text-muted-foreground mt-1">
                Solução completa para distribuidoras de gases e equipamentos
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/')} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Voltar ao Sistema
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-primary">24+</p>
              <p className="text-sm text-muted-foreground">Módulos</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/5 border-emerald-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">8</p>
              <p className="text-sm text-muted-foreground">Categorias</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/5 border-blue-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">100%</p>
              <p className="text-sm text-muted-foreground">Integrado</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-500/5 border-purple-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">Cloud</p>
              <p className="text-sm text-muted-foreground">100% Online</p>
            </CardContent>
          </Card>
        </div>

        {/* Categories and Modules */}
        <div className="space-y-10">
          {categories.map((category) => (
            <section key={category.name}>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-primary rounded-full" />
                {category.name}
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {category.modules.map((module) => {
                  const Icon = module.icon;
                  return (
                    <Card 
                      key={module.name} 
                      className="group hover:shadow-lg hover:border-primary/30 transition-all duration-300 hover:-translate-y-1"
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start gap-3">
                          <div className={`p-2.5 rounded-lg ${module.color} transition-transform group-hover:scale-110`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-base">{module.name}</CardTitle>
                            <CardDescription className="text-xs mt-0.5 line-clamp-2">
                              {module.description}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <ul className="space-y-1">
                          {module.features.map((feature) => (
                            <li 
                              key={feature} 
                              className="text-xs text-muted-foreground flex items-center gap-1.5"
                            >
                              <span className="w-1 h-1 rounded-full bg-primary/60" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t text-center text-muted-foreground">
          <p className="text-sm">
            Sistema desenvolvido para otimizar a gestão de distribuidoras
          </p>
          <p className="text-xs mt-2 opacity-70">
            Versão 2.0 • Todos os módulos integrados em tempo real
          </p>
        </footer>
      </main>
    </div>
  );
};

export default ModulesPresentation;
